/**
 * Remembering your work between launches — every project, not just the last one.
 *
 * Only METADATA is stored — the videos themselves already live in app storage, copied there by
 * the picker, so a saved project just needs to point back at them. That is the whole reason the
 * picker copies rather than holding a blob URL: a blob dies with the page, a path does not. A
 * library video is pointed at by its transcript id instead (its signed link expires in a day).
 *
 * Kept in localStorage: a project is a few kilobytes of metadata plus its sentences. The list
 * is capped, newest first, so the store cannot grow without bound.
 */
const KEY = "cc_mobile_projects_v2";
const LEGACY_KEY = "cc_mobile_project_v1";

/** Bump when the saved shape changes in a way older data cannot satisfy. */
const VERSION = 2;
const KEEP = 12;
export const DEFAULT_ID = "current";

const readAll = () => {
  try {
    const raw = window.localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((p) => p && p.v === VERSION && p.id) : [];
  } catch (e) {
    return [];
  }
};
const writeAll = (list) => {
  try { window.localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* a full or private store is not worth interrupting the user for */ }
};

/** One-time: an older single-project save becomes the first entry of the list. */
const migrate = () => {
  try {
    const raw = window.localStorage.getItem(LEGACY_KEY);
    if (!raw) return;
    const s = JSON.parse(raw);
    window.localStorage.removeItem(LEGACY_KEY);
    if (s && Array.isArray(s.sources) && s.sources.length) {
      writeAll([{ ...s, v: VERSION, id: "legacy", savedAt: s.savedAt || Date.now(), name: describeSaved(s) }, ...readAll()].slice(0, KEEP));
    }
  } catch (e) { /* ignore */ }
};

/** Is this saved project still openable: every phone video has a path, every library video an id. */
const usable = (s) => !!s && Array.isArray(s.sources) && s.sources.length > 0
  && s.sources.every((x) => x && x.duration > 0 && (x.path || (x.remote && x.transcriptId != null)));

/**
 * @param {{ id?, sources, mode, chopSeconds, transcripts, selected, trims, spoken }} state
 */
export function saveProject(state) {
  try {
    if (!state || !state.sources || !state.sources.length) return;
    migrate();
    const id = state.id || DEFAULT_ID;
    const slim = {
      v: VERSION,
      id,
      savedAt: Date.now(),
      mode: state.mode,
      chopSeconds: state.chopSeconds,
      spoken: !!state.spoken,
      // "time" (2/5/10s), "phone" (this phone listened) or "cloud" (CherryClipz transcribed)
      how: state.how || (state.spoken ? "phone" : "time"),
      // `url` is rebuilt from `path` (or a fresh signed link) on load, and `file` (the web
      // fallback's File object) cannot be serialised at all, so neither is stored.
      sources: state.sources.map((s) => ({
        id: s.id, name: s.name, path: s.path || "", duration: s.duration,
        width: s.width || 0, height: s.height || 0,
        remote: !!s.remote, audioOnly: !!s.audioOnly,
        transcriptId: s.transcriptId != null ? s.transcriptId : null,
      })),
      transcripts: state.transcripts || null,
      selected: state.selected || [],
      trims: state.trims || {},
    };
    slim.name = describeSaved(slim);
    const rest = readAll().filter((p) => p.id !== id);
    writeAll([slim, ...rest].slice(0, KEEP));
  } catch (e) { /* ignore */ }
}

/**
 * The most recent usable project, or the one with this id.
 * @returns {object|null}
 */
export function loadProject(id = null) {
  try {
    migrate();
    const list = readAll();
    const s = id == null ? list.find(usable) : list.find((p) => p.id === id);
    return usable(s) ? s : null;
  } catch (e) {
    return null;
  }
}

/** Newest first: what the home screen shows as "Recent". */
export function listProjects() {
  try {
    migrate();
    return readAll().filter(usable).map((p) => ({
      id: p.id,
      name: p.name || describeSaved(p),
      savedAt: p.savedAt || 0,
      kind: p.sources.some((x) => x.remote) ? "library" : "phone",
      how: p.how || (p.spoken ? "phone" : "time"),
      videos: p.sources.length,
      picks: Array.isArray(p.selected) ? p.selected.length : 0,
      spoken: !!p.spoken,
    }));
  } catch (e) {
    return [];
  }
}

export function removeProject(id) {
  try { writeAll(readAll().filter((p) => p.id !== id)); } catch (e) { /* ignore */ }
}

/** Forget one project, or (with no id) all of them. */
export function clearProject(id = null) {
  try {
    if (id == null) { window.localStorage.removeItem(KEY); window.localStorage.removeItem(LEGACY_KEY); }
    else removeProject(id);
  } catch (e) { /* ignore */ }
}

/** A short human line for a project: its one video's name, or "3 videos". */
export const describeSaved = (saved) => {
  if (!saved || !Array.isArray(saved.sources) || !saved.sources.length) return "";
  const n = saved.sources.length;
  return n === 1 ? saved.sources[0].name : `${n} videos`;
};
